/**
 * Sponsor profile page - sponsor profile CRUD for sponsors.
 * 
 * This page allows sponsors to create and manage their sponsor profile,
 * which appears in friend discovery.
 * 
 * Elm-ish architecture: This page is THIN.
 * - Uses useSponsorProfileProgram for state management
 * - Delegates rendering to SponsorProfileView
 * - No duplicated rendering logic
 * 
 * Security:
 * - Only sponsor accounts should access this page
 * - Server-side authorization enforced (sponsor-only endpoints)
 * - Identity-minimized display (no email, account_id, legal name, etc.)
 */
import { useEffect } from 'react';
import { useSponsorProfileProgram } from '@/features/sponsor-profile/useSponsorProfileProgram';
import { SponsorProfileView } from '@/features/sponsor-profile/view';
import { useAuthStore } from '@/store/authStore';

/**
 * Sponsor profile page component.
 * Uses SponsorProfileView for rendering.
 */
export function SponsorProfilePage() {
  const program = useSponsorProfileProgram();
  const registrationKind = useAuthStore(state => state.registrationKind);
  const isSponsor = registrationKind === 'sponsor';

  // Start loading on mount
  useEffect(() => {
    if (isSponsor) {
      program.start();
    }
  }, [isSponsor, program.start]);

  // Show access denied for non-sponsor accounts
  if (!isSponsor) {
    return (
      <div className="page">
        <div className="page-header">
          <h1 className="page-title">Sponsor Profile</h1>
        </div>
        <div className="access-denied" data-testid="access-denied">
          <p>This page is only available for sponsor accounts.</p>
        </div>
        
        <style>{`
          .access-denied {
            padding: var(--spacing);
            background: var(--color-surface);
            border-radius: var(--radius);
            text-align: center;
            color: var(--color-text-muted);
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Sponsor Profile</h1>
        <p className="page-description">
          Create and manage your sponsor profile. Friends will see this when discovering sponsors.
        </p>
      </div>

      <div className="sponsor-profile-content" data-testid="sponsor-profile-content">
        <SponsorProfileView
          model={program.model}
          onStartEdit={program.startEdit}
          onUpdateForm={program.updateForm}
          onSave={program.save}
          onCancel={program.cancel}
          onRetry={program.retry}
          onGenderChanged={program.setGender}
          onGoalChanged={program.setGoal}
          onAgeChanged={program.setAge}
        />
      </div>

      <style>{`
        .sponsor-profile-content {
          margin-top: var(--spacing);
        }
        
        /* Loading state */
        .sponsor-profile-loading {
          padding: var(--spacing);
          text-align: center;
          color: var(--color-text-muted);
        }
        
        /* Error state */
        .sponsor-profile-error {
          padding: var(--spacing);
          background: var(--error-bg, #fee);
          border-radius: var(--radius);
          text-align: center;
        }
        
        .error-text {
          color: var(--error-text, #c44);
          margin-bottom: var(--spacing);
        }
        
        .retry-button {
          padding: 8px 16px;
          background: var(--primary-bg, #4a90d9);
          color: white;
          border: none;
          border-radius: var(--radius);
          cursor: pointer;
        }
        
        .retry-button:hover {
          background: var(--primary-hover, #357abd);
        }
        
        /* Empty state */
        .sponsor-profile-empty {
          padding: var(--spacing-lg);
          background: var(--color-surface);
          border-radius: var(--radius);
          text-align: center;
        }
        
        .empty-hint {
          color: var(--color-text-muted);
          margin: var(--spacing) 0;
        }
        
        /* Profile view */
        .sponsor-profile-view {
          padding: var(--spacing-lg);
          background: var(--color-surface);
          border-radius: var(--radius);
        }
        
        .profile-display {
          margin-bottom: var(--spacing);
        }
        
        .profile-display-name {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: var(--spacing-sm);
        }
        
        .profile-headline {
          color: var(--color-text-muted);
          margin-bottom: var(--spacing);
        }
        
        .profile-bio {
          margin-bottom: var(--spacing);
          line-height: 1.5;
        }
        
        .profile-location {
          display: flex;
          align-items: center;
          gap: 4px;
          color: var(--color-text-muted);
          margin-bottom: var(--spacing);
        }
        
        .profile-updated {
          font-size: 0.875rem;
          color: var(--color-text-muted);
        }
        
        /* Profile form */
        .sponsor-profile-form {
          padding: var(--spacing-lg);
          background: var(--color-surface);
          border-radius: var(--radius);
        }
        
        .form-group {
          margin-bottom: var(--spacing);
        }
        
        .form-group label {
          display: block;
          font-weight: 500;
          margin-bottom: var(--spacing-sm);
        }
        
        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 10px;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          font-size: 1rem;
          box-sizing: border-box;
        }
        
        .form-group textarea {
          resize: vertical;
          font-family: inherit;
        }
        
        .form-group input.input-error,
        .form-group textarea.input-error {
          border-color: var(--error-text, #c44);
        }
        
        .field-error {
          display: block;
          color: var(--error-text, #c44);
          font-size: 0.875rem;
          margin-top: 4px;
        }
        
        .char-count {
          display: block;
          text-align: right;
          font-size: 0.875rem;
          color: var(--color-text-muted);
        }
        
        .form-actions {
          display: flex;
          gap: var(--spacing);
          margin-top: var(--spacing);
        }
        
        /* Saved state */
        .sponsor-profile-saved {
          padding: var(--spacing-lg);
          background: var(--color-surface);
          border-radius: var(--radius);
        }
        
        .saved-message {
          color: var(--color-success, #1e7e34);
          font-weight: 500;
          margin-bottom: var(--spacing);
        }
        
        /* Buttons */
        .btn {
          padding: 10px 20px;
          border-radius: var(--radius);
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          border: 1px solid transparent;
        }
        
        .btn-primary {
          background: var(--primary-bg, #4a90d9);
          color: white;
        }
        
        .btn-primary:hover:not(:disabled) {
          background: var(--primary-hover, #357abd);
        }
        
        .btn-secondary {
          background: var(--color-surface);
          color: var(--color-text);
          border-color: var(--color-border);
        }
        
        .btn-secondary:hover:not(:disabled) {
          background: var(--color-surface-hover, #f5f5f5);
        }
        
        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}